/**
 * ProfileSurveyCard
 *
 * Presentational call-to-action card shown on the renter home page when a
 * member has zero active certifications. It invites the user (or a proxy
 * actor on a member's behalf) to start filling in their profile survey so
 * that future applications can be auto-populated.
 *
 * This component is *purely* presentational: it owns no state, fires no
 * network requests, and does no navigation. The parent (RenterHomePage)
 * supplies all data via props and handles the click via `onClick`.
 *
 * Render states (driven entirely by props):
 *   1. Loading       — `isLoading=true`           → button shows spinner +
 *                                                   "Loading…", disabled.
 *   2. Unavailable   — `isUnavailable=true`       → button reads
 *                                                   "Profile not yet available",
 *                                                   disabled. (e.g. NRE flag
 *                                                   off, 404, or server error.)
 *   3. Proxy ready   — `isProxyMode=true` with
 *                      `proxyMemberFirstName`     → "Complete {Name}'s profile",
 *                                                   enabled.
 *   4. Self ready    — default                    → "Complete your profile",
 *                                                   enabled.
 *
 * If `isProxyMode=true` but no `proxyMemberFirstName` is provided, the
 * component defensively falls back to the self label rather than crashing.
 *
 * The chip, headline, and body copy are identical across every state — only
 * the button's text and disabled flag change.
 */

import { JSX } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export interface ProfileSurveyCardProps {
  /** When true → button shows a spinner and is disabled. */
  isLoading: boolean;
  /**
   * When true (and not loading) → button is disabled, label changes to
   * "Profile not yet available". Use this when the profile survey could
   * not be loaded (e.g. NRE flag off, 404, or server error).
   */
  isUnavailable: boolean;
  /**
   * When true → render proxy variant: button label uses
   * `proxyMemberFirstName`, e.g. "Complete James's profile".
   */
  isProxyMode: boolean;
  /** Required when `isProxyMode === true`. Used in the button label. */
  proxyMemberFirstName?: string;
  /** Click handler. Parent navigates from here. */
  onClick: () => void;
}

/** Resolve the button's visible label and whether the trailing arrow shows. */
function resolveButtonContent(props: ProfileSurveyCardProps): {
  label: string;
  showArrow: boolean;
} {
  if (props.isLoading) {
    return { label: 'Loading…', showArrow: false };
  }
  if (props.isUnavailable) {
    return { label: 'Profile not yet available', showArrow: false };
  }
  if (props.isProxyMode && props.proxyMemberFirstName) {
    return {
      label: `Complete ${props.proxyMemberFirstName}'s profile`,
      showArrow: true,
    };
  }
  return { label: 'Complete your profile', showArrow: true };
}

export default function ProfileSurveyCard(
  props: ProfileSurveyCardProps
): JSX.Element {
  const { isLoading, isUnavailable, onClick } = props;
  const { label, showArrow } = resolveButtonContent(props);
  const disabled = isLoading || isUnavailable;

  return (
    <Box
      sx={{
        backgroundColor: 'primary.dark',
        color: 'primary.contrastText',
        borderRadius: 2,
        boxShadow: 1,
        p: 3,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: 'inherit',
          mb: 1,
        }}
      >
        Save time on every application
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: 'inherit', opacity: 0.85, mb: 3 }}
      >
        Fill in your info once and Pronto uses it automatically whenever you
        apply for housing.
      </Typography>

      <Button
        variant="contained"
        disabled={disabled}
        onClick={onClick}
        endIcon={showArrow ? <ArrowForwardIcon /> : undefined}
        startIcon={
          isLoading ? <CircularProgress size={16} color="inherit" /> : undefined
        }
        data-testid="profile-survey-card-cta"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          px: 2.5,
          py: 1.25,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          color: 'primary.dark',
          '&:hover': {
            backgroundColor: 'background.paper',
            opacity: 0.92,
          },
          '&.Mui-disabled': {
            backgroundColor: 'background.paper',
            color: 'primary.dark',
            opacity: 0.6,
          },
        }}
      >
        <span>{label}</span>
      </Button>
    </Box>
  );
}
